/**
 * In-page review panel shown when the snippet runs in `?hbReviewPage=1` mode.
 *
 * Right-docked floating panel (design ported from the dashboard mockup's
 * review overlay) listing every reviewable change on the current page:
 * filter tabs, per-change cards with Preview / Accept / Reject (+ reason
 * chips), bulk actions, cross-page navigation and a collapsed pill state.
 *
 * Lives inside an open Shadow DOM so customer CSS can't restyle it and the
 * panel's CSS can't bleed back onto the page. All design tokens are defined
 * INSIDE the shadow root (`all:initial` on the host does NOT reset CSS
 * custom properties, so page-level `--ink` etc. would otherwise inherit
 * through the boundary).
 *
 * Contract with index.ts:
 *  - mount(opts) returns `dispose()` that pulls the panel out of the DOM.
 *  - The panel owns UI state (filter, preview toggles, collapse); the caller
 *    owns every DOM/HTTP side effect via callbacks:
 *      onTogglePreview(id, applied) — apply/rollback the patch on the page.
 *      onDecide(id, decision, reason?) — POST the decision; resolve = done,
 *        throw = show the error on the card and leave state unchanged.
 *      onHighlight(id, on) — outline the patch's target element on the page.
 *      onReveal(id) — scroll the patch's target into view and frame it until
 *        the next reveal (click-to-locate).
 *      onNavigate(path) — follow a cross-page link (caller re-appends the
 *        review params).
 *      onClose() — user backed out; caller rolls back previews + cleans URL.
 */

import { panelStrings, rejectReasonChips, formatReviewReason, type WidgetLocale } from './widget-i18n';

export type ReviewChangeState = 'pending' | 'active' | 'rejected' | 'disabled';
export type ReviewPanelDecision = 'approve' | 'reject' | 'reinstate' | 'disable';

export interface ReviewChange {
  id: string;
  state: ReviewChangeState;
  /** Rule id, e.g. "img-alt". */
  rule: string;
  /** First WCAG SC addressed, e.g. "1.1.1" — shown next to the rule. */
  wcag: string;
  /** Human-readable one-liner (patch.description). */
  summary: string;
  /** Why this change was proposed (violation message). */
  why: string;
  /** Plain-text code diff (violation HTML + describeAction lines). */
  code: string;
  /** True when the patch was LLM-generated — shows the "· AI" marker. */
  ai: boolean;
  /** True when the target element is hidden and only its approximate area
      (nearest visible ancestor) can be framed — shows a "hidden area" marker. */
  hiddenArea?: boolean;
  /** True when the element is hidden AND has no visible ancestor to frame (e.g.
      inside a collapsed mobile menu / closed modal) — shows a "collapsed region"
      marker with the region name instead of a frame. */
  hiddenNoArea?: boolean;
  /** Name of the collapsed region the hidden element sits in, e.g. "#mobile-menu"
      — appended to the hiddenNoArea marker. Raw page-derived id/label. */
  hiddenContainer?: string;
}

export interface ReviewPanelOptions {
  domain: string;
  pagePath: string;
  changes: ReviewChange[];
  otherPages: Array<{ path: string; pendingCount: number }>;
  focusPatchId?: string | null;
  /** Unix seconds — shown as "expires in N min" in the panel head. */
  expiresAt: number;
  /** Optional error state — if set, no changes are shown, just the message. */
  error?: string;
  onTogglePreview: (id: string, applied: boolean) => void;
  onDecide: (id: string, decision: ReviewPanelDecision, reason?: string) => Promise<void>;
  onHighlight: (id: string, on: boolean) => void;
  onReveal: (id: string) => void;
  onNavigate: (path: string) => void;
  onClose: () => void;
  /** UI locale for the panel chrome (default 'en'). */
  locale?: WidgetLocale;
}

type Filter = 'pending' | 'active' | 'rejected' | 'all';

// Reject reasons + state labels are locale-aware now — built inside mount from panelStrings().

export function mountReviewPanel(opts: ReviewPanelOptions): { dispose: () => void } {
  const host = document.createElement('div');
  host.id = 'hb-review-host';
  host.style.cssText = 'all:initial;';
  const shadow = host.attachShadow({ mode: 'open' });
  document.body.appendChild(host);

  const S = panelStrings(opts.locale || 'en');
  const STATE_LABELS: Record<ReviewChangeState, string> = {
    pending: S.statePending, active: S.stateActive, rejected: S.stateRejected, disabled: S.stateDisabled,
  };
  // Canonical reason chips (stable code + localized label). Sending the CODE
  // (not the localized label) is what makes rejects aggregatable per rule.
  const REJECT_REASON_LIST = rejectReasonChips(opts.locale || 'en');

  // ── UI state ──────────────────────────────────────────────────────
  const changes = opts.changes.map((c) => ({ ...c }));
  let filter: Filter = 'pending';
  let collapsed = false;
  let showShortcuts = false;
  let reasonOpenId: string | null = null;
  let confirmRejectAll = false;
  let focusedId: string | null = opts.focusPatchId || null;
  /** Pending: in set = previewed ON. Active: in set = previewed OFF. */
  const previewing = new Set<string>();
  const expandedWhy = new Set<string>();
  const expandedCode = new Set<string>();
  const busy = new Set<string>();
  const cardErrors = new Map<string, string>();
  let bulkBusy = false;
  let disposed = false;

  const counts = () => ({
    pending: changes.filter((c) => c.state === 'pending').length,
    active: changes.filter((c) => c.state === 'active').length,
    rejected: changes.filter((c) => c.state === 'rejected').length,
    all: changes.length,
  });
  const filtered = () => changes.filter((c) => filter === 'all' || c.state === filter);
  const byId = (id: string) => changes.find((c) => c.id === id);

  // ── Rendering ─────────────────────────────────────────────────────

  const render = () => {
    if (disposed) return;
    // Full re-render replaces the list element, which resets its scroll
    // position — every click would jump the panel back to the top.
    // Capture and restore it across the innerHTML swap.
    const prevScrollTop = (shadow.querySelector('.rv-list') as HTMLElement | null)?.scrollTop ?? 0;
    const inner = opts.error
      ? renderErrorPanel(opts.error)
      : collapsed
        ? renderPill()
        : renderPanel();
    shadow.innerHTML = `<style>${CSS}</style><div class="hb">${inner}</div>`;
    const list = shadow.querySelector('.rv-list') as HTMLElement | null;
    if (list && prevScrollTop > 0) list.scrollTop = prevScrollTop;
    // Preserve a sensible focus target so shadow-scoped keyboard shortcuts
    // keep working after a re-render wipes the previously focused node.
    if (!collapsed && !opts.error) {
      const reasonInput = shadow.querySelector('input[data-act=reason-input]') as HTMLInputElement | null;
      if (reasonInput) reasonInput.focus({ preventScroll: true });
      else list?.focus({ preventScroll: true });
    }
  };

  const renderPanel = () => {
    const c = counts();
    return `
      <aside class="rv-panel" role="complementary" aria-label="HelpBob review panel">
        ${renderHead(c)}
        ${renderFilters(c)}
        <div class="rv-list" tabindex="0">
          ${filtered().length === 0
            ? `<div class="rv-empty">${S.empty}</div>`
            : filtered().map(renderCard).join('')}
        </div>
        ${renderCrossPage()}
        ${renderFoot(c)}
        ${showShortcuts ? renderShortcuts() : ''}
      </aside>
    `;
  };

  const renderHead = (c: ReturnType<typeof counts>) => {
    const total = c.all;
    const progress = total > 0 ? Math.round(((total - c.pending) / total) * 100) : 100;
    const expiresInMin = Math.max(0, Math.round((opts.expiresAt * 1000 - Date.now()) / 60000));
    return `
      <div class="rv-panel-head">
        <div class="rv-h-brand">
          <div class="rv-h-name">
            <span class="rv-logo">B</span>
            ${S.title}
            <small>${escapeHtml(opts.domain + opts.pagePath)}</small>
          </div>
          <div class="rv-h-icons">
            <button type="button" data-act="collapse" title="${S.collapse}" aria-label="${S.collapse}">${ICONS.chevron}</button>
            <button type="button" data-act="close" title="${S.close}" aria-label="${S.closeAria}">${ICONS.x}</button>
          </div>
        </div>
        <div class="rv-h-meta">
          <span><b>${c.pending}</b> ${S.pending} · ${S.changes(total)}</span>
          <span title="${escapeHtml(S.expiresTitle(expiresInMin))}">${expiresInMin} min · <kbd data-act="shortcuts-toggle" role="button" tabindex="0" aria-label="${S.showShortcuts}">?</kbd></span>
        </div>
        <div class="rv-progress"><div style="width:${progress}%"></div></div>
      </div>
    `;
  };

  const renderFilters = (c: ReturnType<typeof counts>) => {
    const items: Array<{ id: Filter; label: string; ct: number }> = [
      { id: 'pending', label: S.filterPending, ct: c.pending },
      { id: 'active', label: S.filterActive, ct: c.active },
      { id: 'rejected', label: S.filterRejected, ct: c.rejected },
      { id: 'all', label: S.filterAll, ct: c.all },
    ];
    return `
      <div class="rv-filters">
        ${items.map((i) => `
          <button type="button" data-act="filter" data-filter="${i.id}"
            class="rv-filter${filter === i.id ? ' is-active' : ''}">
            ${i.label}<span class="ct">${i.ct}</span>
          </button>
        `).join('')}
      </div>
    `;
  };

  const renderCard = (c: ReviewChange) => {
    const isPreviewing = previewing.has(c.id);
    const isBusy = busy.has(c.id) || bulkBusy;
    const dis = isBusy ? ' disabled' : '';
    const err = cardErrors.get(c.id);

    let actions = '';
    if (c.state === 'pending') {
      actions = `
        <button type="button" class="rv-btn rv-btn-preview${isPreviewing ? ' is-active' : ''}" data-act="preview" data-id="${c.id}"${dis}>
          ${ICONS.eye}<span>${isPreviewing ? S.previewing : S.preview}</span>
        </button>
        <button type="button" class="rv-btn rv-btn-accept" data-act="accept" data-id="${c.id}"${dis}>${ICONS.check}<span>${S.accept}</span></button>
        <button type="button" class="rv-btn rv-btn-reject" data-act="reject" data-id="${c.id}"${dis}>${ICONS.x}<span>${S.reject}</span></button>
      `;
    } else if (c.state === 'active') {
      actions = `
        <button type="button" class="rv-btn rv-btn-preview${isPreviewing ? ' is-active' : ''}" data-act="preview" data-id="${c.id}"${dis}>
          ${isPreviewing ? ICONS.eyeOff : ICONS.eye}<span>${isPreviewing ? S.previewedOff : S.previewOff}</span>
        </button>
        <button type="button" class="rv-btn rv-btn-disable" data-act="disable" data-id="${c.id}"${dis}>${S.disable}</button>
      `;
    } else if (c.state === 'rejected') {
      actions = `<button type="button" class="rv-btn rv-btn-reinstate" data-act="reinstate" data-id="${c.id}"${dis}>${S.reinstate}</button>`;
    } else {
      actions = `<button type="button" class="rv-btn rv-btn-reinstate" data-act="accept" data-id="${c.id}"${dis}>${S.reEnable}</button>`;
    }

    return `
      <article class="rv-card ${c.state}${focusedId === c.id ? ' is-highlight' : ''}" data-card="${c.id}">
        <div class="rv-card-head">
          <span class="rv-tag">${STATE_LABELS[c.state]}${c.ai ? '<span class="rv-ai"> · AI</span>' : ''}</span>
          <span class="rv-rule">${escapeHtml(c.rule)}${c.wcag ? ' · ' + escapeHtml(c.wcag) : ''}</span>
        </div>
        <div class="rv-summary">${escapeHtml(c.summary)}</div>
        ${c.hiddenNoArea
          ? `<div class="rv-area" title="${escapeHtml(S.hiddenNoAreaHint)}">${ICONS.eyeOff}<span>${S.hiddenNoAreaTag}${c.hiddenContainer ? ' «' + escapeHtml(c.hiddenContainer) + '»' : ''}</span></div>`
          : c.hiddenArea
            ? `<div class="rv-area" title="${escapeHtml(S.hiddenAreaHint)}">${ICONS.eyeOff}<span>${S.hiddenAreaTag}</span></div>`
            : ''}
        <div class="rv-expand">
          <button type="button" class="rv-expand-btn" data-act="toggle-why" data-id="${c.id}">${ICONS.chevDown}<span>${S.whyChange}</span></button>
          <button type="button" class="rv-expand-btn" data-act="toggle-code" data-id="${c.id}">${ICONS.chevDown}<span>${S.codeDiff}</span></button>
        </div>
        ${expandedWhy.has(c.id) ? `<div class="rv-why">${escapeHtml(c.why || S.noWhy)}</div>` : ''}
        ${expandedCode.has(c.id) ? `<div class="rv-code">${escapeHtml(c.code)}</div>` : ''}
        <div class="rv-actions">${actions}</div>
        ${err ? `<div class="rv-card-error">${escapeHtml(err)}</div>` : ''}
        ${reasonOpenId === c.id && c.state === 'pending' ? `
          <div class="rv-reject-reason">
            <div class="reasons">
              ${REJECT_REASON_LIST.map((r) => `<button type="button" class="reason-chip" data-act="reject-chip" data-id="${c.id}" data-reason="${escapeHtml(r.code)}">${escapeHtml(r.label)}</button>`).join('')}
            </div>
            <input type="text" data-act="reason-input" data-id="${c.id}" placeholder="${escapeHtml(S.customReason)}" maxlength="500">
          </div>
        ` : ''}
      </article>
    `;
  };

  const renderCrossPage = () => {
    if (!opts.otherPages.length) return '';
    return `
      <div class="rv-crosspage">
        <div class="label">${S.otherPages}</div>
        <div class="links">
          ${opts.otherPages.map((p) => `
            <a href="#" data-act="nav" data-path="${escapeHtml(p.path)}">
              <span>${escapeHtml(p.path)}</span>
              <span class="ct">${p.pendingCount} ${S.pending}</span>
            </a>
          `).join('')}
        </div>
      </div>
    `;
  };

  const renderFoot = (c: ReturnType<typeof counts>) => {
    const dis = bulkBusy || c.pending === 0 ? ' disabled' : '';
    return `
      <div class="rv-foot">
        <div class="lbl">${S.pendingHere(c.pending)}</div>
        <div class="acts">
          <button type="button" class="btn-mini btn-prev" data-act="preview-all"${dis}>${S.previewAll}</button>
          <button type="button" class="btn-mini btn-ok" data-act="accept-all"${dis}>${S.acceptAll}</button>
          <button type="button" class="btn-mini btn-bad" data-act="reject-all"${dis}>${confirmRejectAll ? S.confirm : S.rejectAll}</button>
        </div>
      </div>
    `;
  };

  const renderShortcuts = () => `
    <div class="rv-shortcuts">
      <button type="button" class="close" data-act="shortcuts-close" aria-label="${S.closeShortcuts}">${ICONS.x}</button>
      <h4>${S.shortcutsTitle}</h4>
      <dl>
        <dt><kbd>P</kbd></dt><dd>${S.scPreview}</dd>
        <dt><kbd>A</kbd></dt><dd>${S.scAccept}</dd>
        <dt><kbd>R</kbd></dt><dd>${S.scReject}</dd>
        <dt><kbd>J</kbd> / <kbd>K</kbd></dt><dd>${S.scNav}</dd>
        <dt><kbd>Esc</kbd></dt><dd>${S.scCollapse}</dd>
      </dl>
    </div>
  `;

  const renderPill = () => `
    <button type="button" class="rv-pill" data-act="expand">
      <span class="rv-pill-dot"></span>
      <span><b>HelpBob</b> · ${counts().pending} ${S.pending}</span>
      ${ICONS.chevLeft}
    </button>
  `;

  const renderErrorPanel = (message: string) => `
    <aside class="rv-panel rv-panel-error" role="alert">
      <div class="rv-panel-head">
        <div class="rv-h-brand">
          <div class="rv-h-name"><span class="rv-logo">B</span>${S.title}</div>
          <div class="rv-h-icons">
            <button type="button" data-act="close" title="${S.dismiss}" aria-label="${S.dismiss}">${ICONS.x}</button>
          </div>
        </div>
      </div>
      <div class="rv-error-body">
        <div class="rv-error-title">${S.unavailable}</div>
        <div class="rv-error-msg">${escapeHtml(message)}</div>
      </div>
    </aside>
  `;

  // ── Actions ───────────────────────────────────────────────────────

  const isAppliedNow = (c: ReviewChange) => {
    if (c.state === 'active') return !previewing.has(c.id);
    if (c.state === 'pending') return previewing.has(c.id);
    return false;
  };

  const togglePreview = (id: string) => {
    const c = byId(id);
    if (!c || (c.state !== 'pending' && c.state !== 'active')) return;
    if (previewing.has(id)) previewing.delete(id); else previewing.add(id);
    focusedId = id;
    opts.onTogglePreview(id, isAppliedNow(c));
    render();
  };

  const decide = async (id: string, decision: ReviewPanelDecision, reason?: string) => {
    const c = byId(id);
    if (!c || busy.has(id)) return;
    busy.add(id);
    cardErrors.delete(id);
    render();
    try {
      await opts.onDecide(id, decision, reason);
      c.state = decision === 'approve' ? 'active'
        : decision === 'reject' ? 'rejected'
        : decision === 'reinstate' ? 'pending'
        : 'disabled';
      previewing.delete(id);
      if (reasonOpenId === id) reasonOpenId = null;
    } catch (e) {
      cardErrors.set(id, (e as Error)?.message || S.saveError);
    } finally {
      busy.delete(id);
      render();
    }
  };

  const decideAllPending = async (decision: 'approve' | 'reject') => {
    if (bulkBusy) return;
    bulkBusy = true;
    confirmRejectAll = false;
    render();
    // Sequential on purpose: keeps per-patch audit rows and avoids hammering
    // the engine; pending lists are small (≤ a few dozen).
    for (const c of changes.filter((x) => x.state === 'pending')) {
      await decide(c.id, decision);
    }
    bulkBusy = false;
    render();
  };

  const previewAll = () => {
    for (const c of changes) {
      if (c.state !== 'pending' || previewing.has(c.id)) continue;
      previewing.add(c.id);
      opts.onTogglePreview(c.id, true);
    }
    render();
  };

  const close = () => {
    dispose();
    opts.onClose();
  };

  const moveFocus = (delta: number) => {
    const list = filtered();
    if (!list.length) return;
    const idx = list.findIndex((c) => c.id === focusedId);
    const next = list[Math.min(list.length - 1, Math.max(0, idx === -1 ? 0 : idx + delta))];
    focusedId = next.id;
    render();
    shadow.querySelector(`[data-card="${next.id}"]`)?.scrollIntoView({ block: 'nearest' });
  };

  // ── Event delegation (listeners live on the shadow root, so they survive
  //    full re-renders of its children) ────────────────────────────────

  shadow.addEventListener('click', (ev) => {
    const target = (ev.target as Element | null)?.closest?.('[data-act]') as HTMLElement | null;
    if (!target) {
      // No action element hit — a bare click on a card body reveals (scrolls
      // to + frames) the change's target element on the page.
      const card = (ev.target as Element | null)?.closest?.('[data-card]') as HTMLElement | null;
      if (card) opts.onReveal(card.dataset.card!);
      return;
    }
    const id = target.dataset.id || '';
    switch (target.dataset.act) {
      case 'shortcuts-toggle': showShortcuts = !showShortcuts; render(); break;
      case 'close': close(); break;
      case 'collapse': collapsed = true; showShortcuts = false; render(); break;
      case 'expand': collapsed = false; render(); break;
      case 'filter': filter = target.dataset.filter as Filter; confirmRejectAll = false; render(); break;
      case 'preview': togglePreview(id); break;
      case 'accept': void decide(id, 'approve'); break;
      case 'reject': reasonOpenId = reasonOpenId === id ? null : id; render(); break;
      case 'reject-chip': void decide(id, 'reject', target.dataset.reason); break;
      case 'reinstate': void decide(id, 'reinstate'); break;
      case 'disable': void decide(id, 'disable'); break;
      case 'toggle-why':
        if (expandedWhy.has(id)) expandedWhy.delete(id); else expandedWhy.add(id);
        render();
        break;
      case 'toggle-code':
        if (expandedCode.has(id)) expandedCode.delete(id); else expandedCode.add(id);
        render();
        break;
      case 'preview-all': previewAll(); break;
      case 'accept-all': void decideAllPending('approve'); break;
      case 'reject-all':
        if (confirmRejectAll) { void decideAllPending('reject'); }
        else { confirmRejectAll = true; render(); }
        break;
      case 'nav': ev.preventDefault(); opts.onNavigate(target.dataset.path || '/'); break;
      case 'shortcuts-close': showShortcuts = false; render(); break;
    }
  });

  // Card hover → outline the real element on the page.
  shadow.addEventListener('mouseover', (ev) => {
    const card = (ev.target as Element | null)?.closest?.('[data-card]') as HTMLElement | null;
    if (card) opts.onHighlight(card.dataset.card!, true);
  });
  shadow.addEventListener('mouseout', (ev) => {
    const card = (ev.target as Element | null)?.closest?.('[data-card]') as HTMLElement | null;
    if (card) opts.onHighlight(card.dataset.card!, false);
  });

  // Keyboard shortcuts — scoped to the shadow root: they only fire while
  // focus is inside the panel, so the customer page's own key handling is
  // never hijacked.
  shadow.addEventListener('keydown', (ev) => {
    const kev = ev as KeyboardEvent;
    const tag = (ev.target as Element | null)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') {
      if (kev.key === 'Enter' && (ev.target as HTMLElement).dataset.act === 'reason-input') {
        const input = ev.target as HTMLInputElement;
        const custom = input.value.trim();
        void decide(input.dataset.id || '', 'reject', custom ? formatReviewReason('other', custom) : undefined);
      }
      if (kev.key === 'Escape') { reasonOpenId = null; render(); }
      return;
    }
    // Activate the focused "?" hint (role=button) via Enter/Space.
    if ((kev.key === 'Enter' || kev.key === ' ')
      && (ev.target as HTMLElement | null)?.dataset?.act === 'shortcuts-toggle') {
      ev.preventDefault(); showShortcuts = !showShortcuts; render(); return;
    }
    if (collapsed || opts.error) return;
    switch (kev.key) {
      case '?': showShortcuts = !showShortcuts; render(); break;
      case 'Escape':
        if (showShortcuts) { showShortcuts = false; render(); }
        else { collapsed = true; render(); }
        break;
      case 'j': case 'J': moveFocus(1); break;
      case 'k': case 'K': moveFocus(-1); break;
      case 'p': case 'P': if (focusedId) togglePreview(focusedId); break;
      case 'a': case 'A':
        if (focusedId && byId(focusedId)?.state === 'pending') void decide(focusedId, 'approve');
        break;
      case 'r': case 'R':
        if (focusedId && byId(focusedId)?.state === 'pending') { reasonOpenId = focusedId; render(); }
        break;
    }
  });

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    if (host.parentNode) host.parentNode.removeChild(host);
  };

  render();

  // Scroll the focus patch (the issue the link was minted from) into view
  // and flash its target element once, so the reviewer lands oriented.
  if (focusedId && !opts.error) {
    const id = focusedId;
    shadow.querySelector(`[data-card="${id}"]`)?.scrollIntoView({ block: 'center' });
    opts.onHighlight(id, true);
    setTimeout(() => { if (!disposed) opts.onHighlight(id, false); }, 2500);
  }

  return { dispose };
}

// ── Icons (lucide-style strokes, ported from the dashboard icon set) ──

const icon = (paths: string, size = 16) =>
  `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;

const ICONS = {
  eye: icon('<path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/><circle cx="12" cy="12" r="3"/>', 14),
  eyeOff: icon('<path d="M17.94 17.94A10.07 10.07 0 0 1 12 19c-7 0-11-7-11-7a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 7 11 7a18.65 18.65 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/>', 14),
  check: icon('<polyline points="20 6 9 17 4 12"/>', 14),
  x: icon('<path d="M6 6l12 12"/><path d="M18 6L6 18"/>', 14),
  chevron: icon('<polyline points="9 6 15 12 9 18"/>', 14),
  chevDown: icon('<polyline points="6 9 12 15 18 9"/>', 12),
  chevLeft: icon('<polyline points="15 18 9 12 15 6"/>', 14),
};

// ── Styles (1:1 port of the mockup's .rv-* rules; tokens defined on the
//    inner wrapper so customer-page custom properties can never leak in) ──

const CSS = `
  .hb {
    --yellow: #FCC419;
    --yellow-soft: #FFF1B8;
    --yellow-deep: #E0A800;
    --yellow-ink: #6B5400;
    --ink: #1A1714;
    --ink-2: #4F4A44;
    --ink-3: #6B635A;
    --ink-4: #8C857B;
    --ink-5: #B5AEA3;
    --bg: #F4F1E9;
    --bg-warm: #EFEDE7;
    --paper: #FCFBF6;
    --white: #FFFFFF;
    --line: #E8E5DE;
    --line-2: #DAD6CC;
    --red: #C2255C;
    --red-soft: #FCE6EE;
    --amber: #E08E00;
    --amber-soft: #FDECC5;
    --green: #2F9E44;
    --green-soft: #D7EFDB;
    --display: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
    --body: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
    --mono: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-family: var(--body);
    font-size: 14px;
    line-height: 1.45;
    color: var(--ink);
    -webkit-font-smoothing: antialiased;
  }
  .hb, .hb * { box-sizing: border-box; margin: 0; padding: 0; }
  /* :where() keeps these resets at zero specificity so every component rule
     below wins by source order. The plain ".hb button" form (0-1-1) silently
     beat single-class rules like ".rv-btn-accept" (0-1-0): the Accept button
     rendered transparent with inherited dark text, and on :hover (0-3-0,
     which DID win) the dark hover background appeared under that dark text —
     black-on-black. Same root cause as the invisible collapsed pill. */
  .hb :where(button) { font: inherit; background: none; border: none; cursor: pointer; color: inherit; }
  .hb :where(button):disabled { opacity: 0.5; cursor: progress; }
  .hb :where(a) { text-decoration: none; cursor: pointer; }
  .hb kbd { font-family: var(--mono); }

  .rv-panel {
    position: fixed; right: 16px; top: 16px; bottom: 16px;
    width: 420px;
    background: var(--white);
    border: 1px solid var(--line);
    border-radius: 20px;
    box-shadow: 0 30px 80px -20px rgba(0,0,0,0.55), 0 8px 24px -12px rgba(0,0,0,0.30);
    display: flex; flex-direction: column;
    overflow: hidden;
    z-index: 2147483600;
  }
  /* .hb prefix is required: the generic ".hb button" reset (background:none,
     color:inherit) has higher specificity than a bare ".rv-pill" and turned
     the collapsed pill into invisible text on a white page. */
  .hb .rv-pill {
    position: fixed; right: 16px; top: 16px;
    z-index: 2147483600;
    display: inline-flex; align-items: center; gap: 8px;
    padding: 10px 14px 10px 12px;
    background: var(--ink); color: var(--white);
    border: 1px solid rgba(255,255,255,0.25);
    border-radius: 999px;
    font-size: 13px;
    box-shadow: 0 16px 40px -16px rgba(0,0,0,0.55);
  }
  .hb .rv-pill:hover { background: #2A2622; }
  .rv-pill-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--yellow); box-shadow: 0 0 0 3px rgba(252,196,25,0.20); }

  .rv-panel-head {
    padding: 14px 16px;
    border-bottom: 1px solid var(--line);
    background: var(--ink); color: var(--white);
    display: flex; flex-direction: column; gap: 8px;
  }
  .rv-h-brand { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
  .rv-h-name {
    font-family: var(--display); font-weight: 800; font-size: 13.5px; letter-spacing: -0.01em;
    display: flex; align-items: center; gap: 8px;
  }
  .rv-logo {
    width: 22px; height: 22px; border-radius: 6px; background: var(--yellow);
    display: grid; place-items: center;
    color: var(--ink); font-weight: 900; font-size: 13px; font-family: var(--display);
  }
  .rv-h-name small {
    font-family: var(--mono); font-weight: 500; font-size: 10.5px; color: rgba(255,255,255,0.6);
    margin-left: 6px;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 180px;
  }
  .rv-h-icons { display: flex; gap: 4px; }
  .rv-h-icons button { color: rgba(255,255,255,0.6); padding: 6px; border-radius: 8px; display: inline-flex; }
  .rv-h-icons button:hover { background: rgba(255,255,255,0.08); color: white; }
  .rv-h-meta {
    display: flex; justify-content: space-between; align-items: center;
    font-family: var(--mono); font-size: 10.5px; letter-spacing: 0.08em; text-transform: uppercase;
    color: rgba(255,255,255,0.6);
  }
  .rv-h-meta b { color: var(--yellow); font-weight: 700; }
  .rv-h-meta kbd { background: rgba(255,255,255,0.10); border-radius: 4px; padding: 0 4px; font-size: 9.5px; }
  .rv-h-meta kbd[data-act] { cursor: pointer; }
  .rv-h-meta kbd[data-act]:hover { background: rgba(255,255,255,0.20); }
  .rv-card { cursor: pointer; }
  .rv-progress { height: 4px; background: rgba(255,255,255,0.08); border-radius: 4px; overflow: hidden; }
  .rv-progress > div { height: 100%; background: var(--yellow); transition: width .4s ease; }

  .rv-filters {
    padding: 10px 12px;
    display: flex; gap: 3px; flex-wrap: wrap;
    background: var(--paper);
    border-bottom: 1px solid var(--line);
  }
  .rv-filter {
    padding: 6px 7px;
    border-radius: 999px;
    font-family: var(--mono); font-size: 10px; font-weight: 600; letter-spacing: 0.02em; text-transform: uppercase;
    color: var(--ink-3);
    display: inline-flex; align-items: center; gap: 4px;
  }
  .rv-filter:hover { background: var(--bg-warm); }
  .rv-filter.is-active { background: var(--ink); color: var(--white); }
  .rv-filter .ct { background: rgba(0,0,0,0.06); padding: 1px 5px; border-radius: 999px; font-size: 10px; }
  .rv-filter.is-active .ct { background: rgba(255,255,255,0.18); color: white; }

  .rv-list {
    flex: 1; overflow-y: auto;
    padding: 10px;
    display: flex; flex-direction: column; gap: 8px;
    outline: none;
  }
  .rv-empty { text-align: center; padding: 60px 16px; color: var(--ink-3); font-size: 13px; }

  .rv-card {
    background: var(--white);
    border: 1px solid var(--line);
    border-left: 3px solid var(--ink-5);
    border-radius: 12px;
    padding: 12px 14px;
    flex-shrink: 0;
    transition: border-color .15s ease, transform .15s ease, box-shadow .15s ease;
  }
  .rv-card.is-highlight { box-shadow: 0 0 0 2px var(--yellow); transform: translateY(-1px); }
  .rv-card.pending { border-left-color: var(--amber); background: linear-gradient(0deg, var(--paper) 0%, var(--white) 70%); }
  .rv-card.active { border-left-color: var(--green); }
  .rv-card.rejected { border-left-color: var(--ink-5); opacity: 0.7; }
  .rv-card.disabled { border-left-color: var(--line-2); opacity: 0.7; }
  .rv-card-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 8px; }
  .rv-tag {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 2px 8px; border-radius: 999px;
    font-family: var(--mono); font-size: 10px; font-weight: 700;
    letter-spacing: 0.10em; text-transform: uppercase;
  }
  .rv-tag .rv-ai { color: var(--yellow-ink); }
  .rv-card.pending .rv-tag { background: var(--amber-soft); color: #8a5b00; }
  .rv-card.active .rv-tag { background: var(--green-soft); color: #1e6a2c; }
  .rv-card.rejected .rv-tag { background: var(--bg-warm); color: var(--ink-3); }
  .rv-card.disabled .rv-tag { background: var(--bg-warm); color: var(--ink-3); }
  .rv-rule {
    font-family: var(--mono); font-size: 10px; font-weight: 600;
    letter-spacing: 0.10em; text-transform: uppercase;
    color: var(--ink-4);
    text-align: right;
  }
  .rv-summary { font-size: 13px; color: var(--ink); line-height: 1.45; word-break: break-word; }
  .rv-area {
    margin-top: 8px;
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 9px; border-radius: 999px;
    background: var(--bg-warm); color: var(--ink-3);
    font-family: var(--mono); font-size: 10px; font-weight: 700;
    letter-spacing: 0.06em; text-transform: uppercase;
  }
  .rv-area svg { width: 12px; height: 12px; flex-shrink: 0; }
  .rv-actions { display: flex; gap: 6px; margin-top: 10px; }
  .rv-btn {
    font-family: var(--body); font-weight: 600; font-size: 11.5px;
    padding: 6px 10px; border-radius: 999px;
    display: inline-flex; align-items: center; gap: 4px;
    transition: background .12s ease, color .12s ease, border-color .12s ease;
  }
  .rv-btn-preview { background: var(--bg-warm); color: var(--ink-2); }
  .rv-btn-preview.is-active { background: var(--ink); color: var(--white); }
  .rv-btn-accept { background: var(--ink); color: var(--white); }
  .rv-btn-accept:hover:not(:disabled) { background: #2A2622; }
  .rv-btn-reject { background: var(--white); color: var(--red); border: 1px solid rgba(194,37,92,0.25); }
  .rv-btn-reject:hover:not(:disabled) { background: var(--red-soft); }
  .rv-btn-reinstate { background: var(--bg-warm); color: var(--ink-2); }
  .rv-btn-disable { background: var(--white); color: var(--red); border: 1px solid rgba(194,37,92,0.25); }
  .rv-expand { display: flex; gap: 12px; margin-top: 8px; }
  .rv-expand-btn {
    font-family: var(--mono); font-size: 10px; font-weight: 600; letter-spacing: 0.10em; text-transform: uppercase;
    color: var(--ink-3); display: inline-flex; align-items: center; gap: 4px;
  }
  .rv-expand-btn:hover { color: var(--ink); }
  .rv-why {
    margin-top: 10px; padding: 10px 12px;
    background: var(--bg); border-radius: 10px;
    font-size: 12px; color: var(--ink-2); line-height: 1.5;
  }
  .rv-code {
    margin-top: 8px;
    background: var(--ink); color: var(--white);
    border-radius: 10px; padding: 10px 12px;
    font-family: var(--mono); font-size: 11px; line-height: 1.5;
    white-space: pre-wrap; word-break: break-word;
  }
  .rv-card-error { margin-top: 8px; font-size: 11.5px; font-weight: 500; color: var(--red); }
  .rv-reject-reason {
    margin-top: 10px; padding: 10px 12px;
    background: var(--paper); border-radius: 10px; border: 1px solid var(--line);
  }
  .rv-reject-reason .reasons { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 8px; }
  .rv-reject-reason .reason-chip {
    padding: 5px 10px;
    background: var(--white); border: 1px solid var(--line); border-radius: 999px;
    font-size: 11.5px; cursor: pointer;
  }
  .rv-reject-reason .reason-chip:hover { border-color: var(--ink-3); }
  .rv-reject-reason input {
    width: 100%;
    font: inherit; font-size: 12px;
    padding: 7px 10px;
    background: var(--white);
    border: 1px solid var(--line); border-radius: 8px;
    color: var(--ink);
    outline: none;
  }
  .rv-reject-reason input:focus { border-color: var(--ink-3); }

  .rv-crosspage {
    border-top: 1px solid var(--line);
    padding: 12px 14px;
    background: var(--paper);
  }
  .rv-crosspage .label {
    font-family: var(--mono); font-size: 10px; font-weight: 600;
    letter-spacing: 0.12em; text-transform: uppercase; color: var(--ink-3);
    margin-bottom: 8px;
  }
  .rv-crosspage .links { display: flex; flex-direction: column; gap: 4px; }
  .rv-crosspage a {
    display: flex; justify-content: space-between; align-items: center;
    padding: 7px 10px; border-radius: 8px;
    font-family: var(--mono); font-size: 11.5px; color: var(--ink-2);
  }
  .rv-crosspage a:hover { background: var(--bg-warm); color: var(--ink); }
  .rv-crosspage a .ct {
    background: var(--amber-soft); color: #8a5b00;
    padding: 1px 7px; border-radius: 999px;
    font-size: 10px; font-weight: 700;
  }

  .rv-foot {
    border-top: 1px solid var(--line);
    padding: 12px 14px;
    background: var(--white);
    display: flex; align-items: center; justify-content: space-between; gap: 10px;
  }
  .rv-foot .lbl { font-size: 12px; color: var(--ink-2); }
  .rv-foot .acts { display: flex; gap: 6px; }
  .rv-foot .btn-mini {
    padding: 6px 10px; border-radius: 999px;
    font-family: var(--mono); font-size: 10.5px; font-weight: 600; letter-spacing: 0.10em; text-transform: uppercase;
  }
  .rv-foot .btn-prev { background: var(--bg-warm); color: var(--ink-2); }
  .rv-foot .btn-ok { background: var(--ink); color: var(--white); }
  .rv-foot .btn-bad { background: var(--white); color: var(--red); border: 1px solid rgba(194,37,92,0.25); }

  .rv-shortcuts {
    position: fixed; right: 24px; bottom: 90px;
    width: 340px; background: var(--white); border-radius: 14px; padding: 14px 16px;
    border: 1px solid var(--line);
    box-shadow: 0 8px 24px -16px rgba(20,17,14,0.18), 0 24px 60px -30px rgba(20,17,14,0.20);
    z-index: 2147483601;
  }
  .rv-shortcuts h4 { margin: 0 0 10px; font-family: var(--display); font-weight: 700; font-size: 13px; }
  .rv-shortcuts dl { display: grid; grid-template-columns: 60px 1fr; gap: 6px 12px; margin: 0; font-size: 12px; }
  .rv-shortcuts kbd {
    font-family: var(--mono); font-size: 11px; font-weight: 600;
    background: var(--bg-warm); border-radius: 6px; padding: 2px 7px; border: 1px solid var(--line-2);
  }
  .rv-shortcuts .close { position: absolute; top: 8px; right: 8px; color: var(--ink-4); padding: 4px 6px; display: inline-flex; }

  .rv-error-body { padding: 24px 20px; }
  .rv-error-title { font-family: var(--display); font-weight: 700; font-size: 14px; margin-bottom: 6px; }
  .rv-error-msg { font-size: 13px; color: var(--ink-2); line-height: 1.5; }
  .rv-panel-error { bottom: auto; }

  @media (max-width: 520px) {
    .rv-panel { left: 12px; right: 12px; top: auto; bottom: 12px; width: auto; max-height: 70vh; }
    .hb .rv-pill { top: auto; bottom: 16px; }
    .rv-shortcuts { right: 16px; left: 16px; width: auto; }
  }
`;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => (
    ch === '&' ? '&amp;' :
    ch === '<' ? '&lt;' :
    ch === '>' ? '&gt;' :
    ch === '"' ? '&quot;' : '&#39;'
  ));
}
